import styled, { css, type RuleSet } from 'styled-components'
import {
	black,
	blackOrange,
	blue,
	blueDark,
	border2,
	darkOrange,
	gold,
	green,
	grey,
	orange,
	red,
	success,
	textMuted,
	warmTint,
	white,
} from '@/theme/colors'
import { radii } from '@/theme/tokens'
import { text } from '@/theme/typography'

export type PillTone =
	| 'accent'
	| 'soft'
	| 'muted'
	| 'success'
	| 'gold'
	| 'danger'
	| 'cool'
	| 'coolSoft'
	| 'dark'
	| 'outline'
export type PillSize = 'sm' | 'md' | 'lg'

const tones: Record<PillTone, RuleSet<object>> = {
	/** The category or tag that names a thing: the orange fill. */
	accent: css`
		background: ${orange};
		color: ${white};
	`,
	/** A quiet warm chip: a date, a pending state. */
	soft: css`
		background: ${warmTint};
		color: ${darkOrange};
	`,
	/** Counts and times: grey ground, muted text. */
	muted: css`
		background: ${grey};
		color: ${textMuted};
	`,
	/** "You" markers and connected states. */
	success: css`
		background: ${success}1f;
		color: ${green};
	`,
	/** Split-type and payment-detail badges. */
	gold: css`
		background: ${gold}1f;
		color: ${gold};
	`,
	/** Deleted, failed. */
	danger: css`
		background: ${red};
		color: ${white};
	`,
	/** System markers on the blue: internal, imported. */
	cool: css`
		background: ${blue};
		color: ${white};
	`,
	/** Tags on a blue tint. */
	coolSoft: css`
		background: ${blue}14;
		color: ${blueDark};
	`,
	/** A label over imagery: manual flights, attachment counts. */
	dark: css`
		background: ${blackOrange}cc;
		color: ${white};
	`,
	/** The hairline pill: day counts, the read-only marker. */
	outline: css`
		background: ${white};
		color: ${black};
		border-color: ${border2};
	`,
}

/* Padding sits inside a 1px border, so the visual box reads 3/9, 5/12 and 6/16. */
const sizes: Record<PillSize, RuleSet<object>> = {
	sm: css`
		${text.badge}
		padding: 2px 8px;
	`,
	md: css`
		${text.labelSm}
		padding: 4px 11px;
	`,
	lg: css`
		${text.label}
		padding: 5px 15px;
	`,
}

/**
 * The app's one pill: a short label on a full-radius ground — a category, a
 * count, a state, a tag. Tone carries the meaning, size the setting; pages keep
 * their own names through presets, `styled(Pill).attrs({ $tone: 'gold' })`.
 */
export const Pill = styled.span<{ $tone?: PillTone; $size?: PillSize }>`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	width: fit-content;
	vertical-align: middle;
	white-space: nowrap;
	border: 1px solid transparent;
	border-radius: ${radii.pill};
	${({ $size = 'sm' }) => sizes[$size]}
	${({ $tone = 'accent' }) => tones[$tone]}
`
